import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, DownloadIcon, Modal, Radio, Table } from '@gamesb42/ui-kit';

import { getToken } from 'helpers/token';
import { isAdmin, isAML } from 'helpers/token';
import documentSvg from './assets/doc.svg';
import s from './styles.module.scss';
import { downloadFileOfBlob } from 'helpers/file';
import baseRequest from 'queries/baseRequest';
import { changeStatusCompany, createCompanyAgreement } from 'api/company';
import { CompanyStatus } from 'types/CompanyTypes';
import { useUserContext } from 'contexts/UserProvider';

const getRadioStatus = (companyStatus?: string) => {
  if (companyStatus === 'rejected') return 'rejected';
  if (companyStatus === 'verified' || companyStatus === 'active') return 'verified';

  return '';
};

export function AMLKYC({ companyData, refreshCompany, onAgreementCreated }: any) {
  //@ts-ignore
  const { companyId } = useParams();
  const [filesData, setFilesData] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingZip, setLoadingZip] = useState(false);
  const [status, setStatus] = useState(getRadioStatus(companyData.status));
  const { user } = useUserContext();
  // Убираем локальные переменные isAdmin и isAML

  const columns = [
    {
      title: 'Document type',
      dataIndex: 'displayed_doc_type',
    },
    {
      title: 'File',
      render: (_: any, record: any) => {
        const isFileName = record.file?.name;

        function renderColumn() {
          if (isFileName) {
            return (
              <div className={s.fileRow}>
                {isFileName && <img src={documentSvg} alt="icon" />}
                <div
                  onClick={() => {
                    if (isFileName) handleDownloadClick(record.file.id, isFileName);
                  }}
                >
                  {isFileName}
                </div>
              </div>
            );
          } else {
            return <div className={s.fileRowNoData}>-</div>;
          }
        }

        return renderColumn();
      },
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      render: (_: any, record: any) => {
        if (record?.comment?.length > 0) {
          return <div>{record?.comment}</div>;
        } else {
          return <div>-</div>;
        }
      },
    },
  ];

  const handleDownloadClick = async (fileId: string, fileNameIn: string) => {
    try {
      // Для скачивания файлов нужен специальный запрос, который возвращает blob
      const response = await fetch(`${process.env.REACT_APP_SCA_URL}/v1/documents/file/${fileId}`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const blob = await response.blob();
      downloadFileOfBlob(blob, 'application/pdf', fileNameIn, true);
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
    }
  };

  const handleDownloadAllClick = async () => {
    setLoadingZip(true);
    let fileName = 'files.zip';

    try {
      // Для скачивания файлов нужен специальный запрос, который возвращает blob
      const response = await fetch(`${process.env.REACT_APP_SCA_URL}/v1/documents/company/${companyId}/zip`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const xFileName = response.headers.get('x-file-name');
      if (xFileName) {
        fileName = xFileName;
      }

      const blob = await response.blob();
      downloadFileOfBlob(blob, 'typeLocal', fileName, false);
    } catch (error) {
      console.error('Ошибка при скачивании архива:', error);
    } finally {
      setLoadingZip(false);
    }
  };

  useEffect(() => {
    setStatus(getRadioStatus(companyData.status));
  }, [companyData.status]);

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        const result = await baseRequest({
          path: `${process.env.REACT_APP_SCA_URL}/v1/documents/company/${companyId}`,
          options: {
            method: 'GET',
          },
        });
        setFilesData(result);
      } catch (error) {
        console.error('Ошибка при получении документов:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [companyId]);

  return (
    <div className={s.wrapper}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ width: '100%' }}>
            {(isAdmin() || isAML()) && (
              <Radio.Group value={status} disabled={companyData.status !== 'in-review'}>
                <Radio
                  onChange={() => {
                    Modal.warning({
                      title: (
                        <>
                          Confirm the status «<span style={{ color: '#0E9C1C' }}>Verified</span>»?
                        </>
                      ),
                      okText: 'Confirm',
                      okButtonProps: {
                        className: s.del,
                      },
                      onOk: async () => {
                        const previousStatus = status;

                        setStatus('verified');

                        try {
                          await createCompanyAgreement(companyId);
                        } catch {
                          setStatus(previousStatus);

                          return;
                        }

                        // SCA разрешает только пошаговые переходы, in-review -> active нужен force
                        const isStatusChanged = await changeStatusCompany(companyId, 'active' as CompanyStatus, true)
                          .then(() => true)
                          .catch(() => false);

                        await refreshCompany();
                        onAgreementCreated();

                        if (!isStatusChanged) setStatus(previousStatus);
                      },
                    });
                  }}
                  value={'verified'}
                >
                  <span style={{ color: '#26B634' }}>Verified</span>
                </Radio>
                <Radio
                  onChange={() => {
                    Modal.warning({
                      title: (
                        <>
                          Confirm the status «<span style={{ color: '#EC3E72' }}>Rejected</span>»?
                        </>
                      ),
                      okText: 'Confirm',
                      okButtonProps: {
                        className: s.del,
                      },
                      onOk: () => {
                        const previousStatus = status;

                        setStatus('rejected');

                        return changeStatusCompany(companyId, 'rejected' as CompanyStatus)
                          .then(() => refreshCompany())
                          .catch(() => {
                            setStatus(previousStatus);
                          });
                      },
                    });
                  }}
                  value={'rejected'}
                >
                  <span style={{ color: '#EC3E72' }}>Rejected</span>
                </Radio>
              </Radio.Group>
            )}
          </div>
          <div
            style={{
              width: !isAdmin() ? '100%' : '',
              display: !isAdmin() ? 'flex' : '',
              justifyContent: !isAdmin() ? 'flex-end' : '',
            }}
          >
            <Button
              style={{ cursor: 'pointer' }}
              onClick={handleDownloadAllClick}
              disabled={loadingZip}
              icon={<DownloadIcon size={24} />}
            >
              Download
            </Button>
          </div>
        </div>
      </div>
      <Table
        className={s.table}
        columns={columns}
        dataSource={filesData}
        loading={loading}
        scroll={{ y: 'max-content' }}
      />
    </div>
  );
}
